import {LicensedItem, FamiliesLicensedItem, SoundPackagesLicensedItem} from 'powtoon-services/licensing/types'
import {
  enterEffectsList,
  AudioData,
  TextToSpeechAudioData,
  VideoData,
  VideoRecordingData,
  ImageData,
  ModelData
} from 'powtoon-versioning/src/currentSchema'

export enum AssetType {
  CHARACTER = 'character',
  OBJECT = 'object',
  IMAGE_HOLDER = 'imageHolder',
  FEATURE = 'feature',
  BACKGROUND = 'background',
  VIDEO = 'video',
  COMPOSITE_ASSET = 'compositeAsset',
  SCENE = 'scene',
  TEMPLATE = 'template',
  FONT = 'font',
  MUSIC = 'music',
  SOUNDEFFECT = 'soundeffect',
  IMAGE = 'image',
  AVATAR = 'avatar'
}

export enum ItemType {
  SOUND = 'sound',
  COLOR = 'color',
  VECTOR_GRAPHICS = 'vectorGraphics',
  IMAGE = 'image',
  VIDEO = 'video',
  MOVIE_CLIP = 'movieClip',
  SPINE = 'spine',
  FYBER = 'fyber',
  TEXT = 'text',
  FEATURE = 'feature',
  FONT = 'font',
  POWTOON = 'powtoon',
  ACTOR = 'actor'
}

export enum VideoFileFormat {
  MP4 = 'mp4',
  MP4_RGBA = 'mp4-rgba'
}

interface WithDimensions {
  width: number | null,
  height: number | null
}

interface WithEnterExitEffects {
  enterEffect: enterEffectsList | null | '',
  exitEffect: enterEffectsList | null | ''
}

interface WithContentURLs {
  thumbUrl: string,
  previewUrl: string,
  dataUrl: string
}

interface WithFamilies extends FamiliesLicensedItem {
  visualStyles?: string[]
}

export interface BaseLibraryItem extends LicensedItem {
  id: string,
  name: string,
  source: string,
  fileFormat: string
}

export interface BackgroundLibraryItem extends BaseLibraryItem, WithDimensions, WithContentURLs, WithFamilies { // ??
  assetType: AssetType.BACKGROUND,
  itemType: ItemType.IMAGE | ItemType.VIDEO | ItemType.VECTOR_GRAPHICS
  modelData: ModelData
}

export interface CompositeAssetLibraryItem extends BaseLibraryItem {
  assetType: AssetType.COMPOSITE_ASSET,
  itemType: ItemType.POWTOON,
  thumbUrl: string,
  isPrototype?: boolean,
  variationOf?: string | null
}

export interface FontLibraryItem extends WithContentURLs {
  family: string,
  name: string,
  assetType: AssetType.FONT,
  itemType: ItemType.FONT,
  fileFormat: string,
  hasBold: boolean,
  hasItalic: boolean,
  tags: string[],
  order?: number, // what is this?
  creationDate?: string
}

export interface BaseCharacterOrObjectLibraryItem extends
  BaseLibraryItem, WithDimensions, WithEnterExitEffects, WithContentURLs, WithFamilies {
    assetType: AssetType.CHARACTER | AssetType.OBJECT,
    animationLoops: number | null,
    maintainAspectRatio: boolean,
    isColorable: boolean,
    isPrototype: boolean,
    isTintable: boolean,
    modelData: ModelData | null,
    iconUrl: string | null,
    variationOf?: string | null
  }

export interface FyberLibraryItem extends BaseCharacterOrObjectLibraryItem {
  assetType: AssetType.CHARACTER,
  itemType: ItemType.FYBER,
  isEditable: boolean,
  isLipsyncSupported: boolean,
  animationId?: string | null,
  ownerId?: string,
  pose?: string | null
}

export interface SpineLibraryItem extends BaseCharacterOrObjectLibraryItem {
  assetType: AssetType.CHARACTER,
  itemType: ItemType.SPINE
}

export interface VectorGraphicsLibraryItem extends
  BaseCharacterOrObjectLibraryItem, WithEnterExitEffects, WithDimensions {
    itemType: ItemType.VECTOR_GRAPHICS
  }

export interface MovieClipLibraryItem extends BaseCharacterOrObjectLibraryItem, WithEnterExitEffects, WithDimensions {
  itemType: ItemType.MOVIE_CLIP
}

export interface ImageLibraryItem extends BaseLibraryItem, WithDimensions, WithEnterExitEffects, WithContentURLs {
  assetType?: AssetType.IMAGE,
  itemType: ItemType.IMAGE,
  imageData?: ImageData | null,
  ownerId?: string,
}

export interface ImageHolderLibraryItem
  extends BaseLibraryItem, WithDimensions, WithEnterExitEffects, WithContentURLs, WithFamilies {
    assetType: AssetType.IMAGE_HOLDER,
    itemType: ItemType.VECTOR_GRAPHICS,
    isTintable: boolean,
    isColorable: boolean,
    maintainAspectRatio: boolean,
    modelData: ModelData | null
  }

export interface VideoLibraryItem extends BaseLibraryItem, WithDimensions, WithContentURLs, WithFamilies {
  itemType: ItemType.VIDEO,
  videoData: VideoData | VideoRecordingData,
  expiryDate?: string,
  assetType?: AssetType.VIDEO,
  ownerId?: string
}


export interface SceneLibraryItem extends BaseLibraryItem {
  assetType: AssetType.SCENE,
  itemType: ItemType.POWTOON,
  ownerId: string,
  thumbUrl: string,
  duration: number,
  groupId?: string
}

export interface SoundLibraryItem extends BaseLibraryItem, SoundPackagesLicensedItem {
  itemType: ItemType.SOUND,
  audioType: 'voiceover' | 'soundtrack',
  length: number,
  dataUrl: string
  assetType?: AssetType.MUSIC,
  audioData?: AudioData | TextToSpeechAudioData | null,
  ownerId?: string,
  folders?: string[]
}

export interface SoundEffectLibraryItem extends Omit<SoundLibraryItem, 'assetType' | 'audioType'> {
  assetType: AssetType.SOUNDEFFECT,
  audioType: 'soundeffect',
  category: string
  effectIds?: string[]
  loopStart?: number | null,
  loopEnd?: number | null
}

export interface BaseAvatarLibraryItem extends WithDimensions, WithContentURLs {
  id: string,
  name: string | null,
  itemType: ItemType.VIDEO | ItemType.ACTOR,
  assetType: AssetType.AVATAR,
  source: 'powtoon',
  maintainAspectRatio?: boolean
}

export interface ActorLibraryItem extends BaseAvatarLibraryItem {
  name: string,
  itemType: ItemType.ACTOR,
  recommendedSpeakers?: string[],
  pose?: string,
  emotion?: string | null,
  isPrototype?: boolean,
  variationOf?: string | null,
  fileFormat?: string
}

export interface AvatarVideoLibraryItem extends BaseAvatarLibraryItem {
  itemType: ItemType.VIDEO,
  assetType: AssetType.AVATAR,
  actorVariationId: string,
  voiceoverId: string,
  videoData: VideoData,
  fileFormat: 'mp4-rgba',
  name: string | null,
  ownerId?: string
}

export interface UserActorLibraryItem extends ActorLibraryItem {
  ownerId: string
}

export type AvatarLibraryItem = AvatarVideoLibraryItem | ActorLibraryItem

export type AnyLibraryItem =
  BackgroundLibraryItem | CompositeAssetLibraryItem | FontLibraryItem | FyberLibraryItem | SpineLibraryItem |
  VectorGraphicsLibraryItem | MovieClipLibraryItem | ImageLibraryItem | ImageHolderLibraryItem | VideoLibraryItem |
  SceneLibraryItem | SoundLibraryItem | SoundEffectLibraryItem | ActorLibraryItem | BaseCharacterOrObjectLibraryItem |
  AvatarVideoLibraryItem | UserActorLibraryItem

export type RawLibraryItem<T extends AnyLibraryItem> = T & {[key: string]: any}

export type AnyRawLibraryItem = RawLibraryItem<AnyLibraryItem>
